/**
 * Clip colour correction (0.96.0), defined once as maths on gamma-encoded RGB in 0…1, so the browser preview (an
 * SVG filter) and the export (FFmpeg colorchannelmixer + lutrgb) do exactly the same thing:
 *
 *   1. a 3×3 matrix M = Sat(saturation) · diag(gR, gG, gB), where
 *        g = 2^exposure, gR = g·(1 + 0.1·temperature), gG = g·(1 − 0.1·tint), gB = g·(1 − 0.1·temperature)
 *      (warm = more red and less blue; positive tint = magenta, less green), and Sat is the luma-preserving
 *      saturation matrix with Rec.709 weights: s·I + (1 − s)·[w; w; w], w = (0.2126, 0.7152, 0.0722);
 *      the result is clamped to 0…1 (both the SVG filter and an 8-bit RGB filter clamp there);
 *   2. contrast as a linear curve pivoting on 0.5: (v − 0.5)·(1 + contrast) + 0.5, clamped to 0…1.
 *
 * Fades and transitions apply after it, as before.
 */
import { z } from 'zod';

export const ColorCorrectionSchema = z.object({
  /** Stops, −2…+2 (0 = neutral). */
  exposure: z.number().min(-2).max(2),
  /** −1…+1 around a 0.5 pivot (0 = neutral). */
  contrast: z.number().min(-1).max(1),
  /** 0 (grey) … 2 (1 = neutral). */
  saturation: z.number().min(0).max(2),
  /** −1 (cool) … +1 (warm). */
  temperature: z.number().min(-1).max(1),
  /** −1 (green) … +1 (magenta). */
  tint: z.number().min(-1).max(1),
});
export type ColorCorrection = z.infer<typeof ColorCorrectionSchema>;

export const NEUTRAL_COLOR: ColorCorrection = { exposure: 0, contrast: 0, saturation: 1, temperature: 0, tint: 0 };
export const COLOR_RANGES: Record<keyof ColorCorrection, { min: number; max: number; step: number }> = {
  exposure: { min: -2, max: 2, step: 0.05 },
  contrast: { min: -1, max: 1, step: 0.01 },
  saturation: { min: 0, max: 2, step: 0.01 },
  temperature: { min: -1, max: 1, step: 0.01 },
  tint: { min: -1, max: 1, step: 0.01 },
};

/** Rec.709 luma weights. */
export const REC709 = [0.2126, 0.7152, 0.0722] as const;

export function isNeutralColor(c: ColorCorrection | null | undefined): boolean {
  return !c || (c.exposure === 0 && c.contrast === 0 && c.saturation === 1 && c.temperature === 0 && c.tint === 0);
}

/** A correction within its ranges (values rounded to 1e-4 so documents stay tidy), or null when neutral. */
export function tidyColor(c: Partial<ColorCorrection> | null | undefined): ColorCorrection | null {
  if (!c) return null;
  const r = (k: keyof ColorCorrection) => {
    const v = Number(c[k] ?? NEUTRAL_COLOR[k]);
    const { min, max } = COLOR_RANGES[k];
    return Math.round(Math.min(max, Math.max(min, Number.isFinite(v) ? v : NEUTRAL_COLOR[k])) * 1e4) / 1e4;
  };
  const out: ColorCorrection = { exposure: r('exposure'), contrast: r('contrast'), saturation: r('saturation'), temperature: r('temperature'), tint: r('tint') };
  return isNeutralColor(out) ? null : out;
}

/** The combined 3×3 matrix (row-major: out_r = m[0]·r + m[1]·g + m[2]·b, …) with the gains and the saturation folded in. */
export function colorMatrix(c: ColorCorrection): number[] {
  const g = Math.pow(2, c.exposure);
  const gains = [g * (1 + 0.1 * c.temperature), g * (1 - 0.1 * c.tint), g * (1 - 0.1 * c.temperature)];
  const s = c.saturation;
  const m: number[] = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      const sat = (row === col ? s : 0) + (1 - s) * REC709[col]!;
      m.push(sat * gains[col]!);
    }
  }
  return m;
}

/** The contrast curve: v·slope + intercept. */
export function contrastCurve(c: ColorCorrection): { slope: number; intercept: number } {
  const slope = 1 + c.contrast;
  return { slope, intercept: 0.5 * (1 - slope) };
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** The correction of one gamma-encoded RGB colour in 0…1: the definition both renderers follow. */
export function applyColor(c: ColorCorrection, rgb: readonly [number, number, number]): [number, number, number] {
  const m = colorMatrix(c);
  const { slope, intercept } = contrastCurve(c);
  const out: [number, number, number] = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const v = clamp01(m[i * 3]! * rgb[0] + m[i * 3 + 1]! * rgb[1] + m[i * 3 + 2]! * rgb[2]);
    out[i] = clamp01(v * slope + intercept);
  }
  return out;
}

const n = (v: number) => (Math.abs(v) < 1e-12 ? 0 : v).toFixed(6);

/** The SVG filter's feColorMatrix values (4×5, row-major, alpha unchanged). */
export function svgColorMatrixValues(c: ColorCorrection): string {
  const m = colorMatrix(c);
  return [m[0], m[1], m[2], 0, 0, m[3], m[4], m[5], 0, 0, m[6], m[7], m[8], 0, 0, 0, 0, 0, 1, 0].map((v) => n(v!)).join(' ');
}

/**
 * The FFmpeg filters (6.1 and 7.x): colorchannelmixer with the combined matrix, then lutrgb for the contrast curve
 * and the clamp, on 8-bit RGB (`alpha`: rgba, the alpha channel passes through).
 */
export function ffmpegColorFilters(c: ColorCorrection, alpha: boolean): string {
  const m = colorMatrix(c);
  const { slope } = contrastCurve(c);
  const mix = `colorchannelmixer=rr=${n(m[0]!)}:rg=${n(m[1]!)}:rb=${n(m[2]!)}:gr=${n(m[3]!)}:gg=${n(m[4]!)}:gb=${n(m[5]!)}:br=${n(m[6]!)}:bg=${n(m[7]!)}:bb=${n(m[8]!)}${alpha ? ':aa=1' : ''}`;
  const curve = `clip((val-127.5)*${n(slope)}+127.5,0,255)`;
  return `format=${alpha ? 'rgba' : 'rgb24'},${mix}${c.contrast !== 0 ? `,lutrgb=r='${curve}':g='${curve}':b='${curve}'` : ''}`;
}
